import { Link, useLocation } from 'react-router';
import { useThemeColors } from '../../hooks/useThemeColors';

function VisionIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

/* PRI-310: DocsIcon parked while the Docs tab is hidden. Restore with the tab.
function DocsIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
*/

function MoreIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

function AccountIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// PRI-310: Docs tab hidden during friendly-tester phase. Restore when Web SDK EPIC ships.
// PRI-512: the More tab's fallback route was /use-cases (About content) — that
// content moved to www; the fallback now routes to /pricing (still in-app).
const MORE_SENTINEL = '#more';
const TAB_ITEMS = [
  { label: 'Vision', href: '/', icon: VisionIcon },
  { label: 'More', href: MORE_SENTINEL, icon: MoreIcon },
  { label: 'Account', href: '/dashboard', icon: AccountIcon },
];

interface MobileTabBarProps {
  // Optional callback to open the sidebar to the 'about' panel instead of routing to /use-cases
  onOpenAbout?: () => void;
  // Callback to open the More bottom sheet panel
  onOpenMore?: () => void;
  // Whether the More panel is currently open (drives active state)
  moreActive?: boolean;
}

export default function MobileTabBar({ onOpenAbout, onOpenMore, moreActive = false }: MobileTabBarProps = {}) {
  const location = useLocation();
  const c = useThemeColors();

  return (
    <div
      data-testid="mobile-tab-bar"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 'calc(49px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: c.bgSurface,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: `1px solid ${c.border}`,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
      }}
    >
      {TAB_ITEMS.map(item => {
        const isActive = item.href !== MORE_SENTINEL && (
          location.pathname === item.href
          || (item.href !== '/' && location.pathname.startsWith(item.href))
        );
        // More tab: open bottom sheet panel via callback (preferred), or fall back to onOpenAbout for back-compat
        if (item.href === MORE_SENTINEL) {
          const moreHandler = onOpenMore ?? onOpenAbout;
          const moreIsActive = moreActive;
          if (moreHandler) {
            return (
              <button
                key={item.href}
                onClick={moreHandler}
                style={{
                  flex: 1, height: 49, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  color: moreIsActive ? c.signal : c.textMuted,
                  fontSize: 12, fontWeight: 500,
                  background: 'none', border: 'none', cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  fontFamily: 'inherit',
                }}
              >
                <item.icon size={24} />
                <span className="tab-label" style={{ marginTop: 2 }}>{item.label}</span>
              </button>
            );
          }
        }
        // PRI-512: More-without-handler falls back to /pricing (in-app), not the removed /use-cases
        return (
          <Link
            key={item.href}
            to={item.href === MORE_SENTINEL ? '/pricing' : item.href}
            style={{
              flex: 1, height: 49, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              color: isActive ? c.signal : c.textMuted,
              fontSize: 12, fontWeight: 500,
              textDecoration: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <item.icon size={24} />
            <span className="tab-label" style={{ marginTop: 2 }}>{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
}
